import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader, Hash, Copy, ChevronRight, Download, Save } from "lucide-react";
import { cn, copyFormattedContent } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Utility function to get color classes
const getColorClass = (color: string, type: string, variant: string) => {
  const colorMap: Record<string, Record<string, Record<string, string>>> = {
    teal: {
      bg: {
        '50': 'bg-teal-50',
        '50/50': 'bg-teal-50/50',
        '900/10': 'bg-teal-900/10',
        '900/20': 'bg-teal-900/20'
      },
      text: {
        '700': 'text-teal-700',
        '400': 'text-teal-400',
        '300': 'text-teal-300'
      },
      border: {
        '100': 'border-teal-100',
        '200': 'border-teal-200',
        '800/30': 'border-teal-800/30'
      }
    },
    blue: {
      bg: {
        '50': 'bg-blue-50',
        '50/50': 'bg-blue-50/50',
        '900/10': 'bg-blue-900/10',
        '900/20': 'bg-blue-900/20'
      },
      text: {
        '700': 'text-blue-700',
        '400': 'text-blue-400',
        '300': 'text-blue-300'
      },
      border: {
        '100': 'border-blue-100',
        '200': 'border-blue-200',
        '800/30': 'border-blue-800/30'
      }
    },
    purple: {
      bg: {
        '50': 'bg-blue-50',
        '50/50': 'bg-blue-50/50',
        '900/10': 'bg-blue-900/10',
        '900/20': 'bg-blue-900/20'
      },
      text: {
        '700': 'text-blue-700',
        '400': 'text-blue-400',
        '300': 'text-blue-300'
      },
      border: {
        '100': 'border-blue-100',
        '200': 'border-blue-200',
        '800/30': 'border-blue-800/30'
      }
    },
    emerald: {
      bg: {
        '50': 'bg-emerald-50',
        '50/50': 'bg-emerald-50/50',
        '900/10': 'bg-emerald-900/10',
        '900/20': 'bg-emerald-900/20'
      },
      text: {
        '700': 'text-emerald-700',
        '400': 'text-emerald-400',
        '300': 'text-emerald-300'
      },
      border: {
        '100': 'border-emerald-100',
        '200': 'border-emerald-200',
        '800/30': 'border-emerald-800/30'
      }
    }
  };

  return colorMap[color]?.[type]?.[variant] || '';
};

// Define interface for the keywords structure
interface KeywordsData {
  core: string[];
  competitive: string[];
  niche: string[];
  longTail?: string[];
  long_tail?: string[];
}

interface AnalysisResults {
  keywords?: KeywordsData;
}

const KeywordsPage = () => {
  // Initialize with empty arrays structure rather than null
  const [results, setResults] = useState<AnalysisResults>({
    keywords: {
      core: [],
      competitive: [],
      niche: [],
      longTail: []
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const { id: projectId } = useParams();
  const { toast } = useToast();
  const [copiedItem, setCopiedItem] = useState<{ type: 'keyword' | 'group'; id: string } | null>(null);

  useEffect(() => {
    const fetchResults = async () => {
      if (!projectId) return;
      setIsLoading(true);

      try {
        const { data: crawl, error } = await supabase
          .from('website_crawls')
          .select('id, analysis_results, status, created_at')
          .eq('project_id', projectId)
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          console.error('Error fetching results:', error);
          throw error;
        }

        console.log('Raw crawl data:', crawl);
        
        // Default structure with empty arrays - ensures we always have a valid structure
        const defaultKeywords = {
          keywords: {
            core: [],
            competitive: [],
            niche: [],
            longTail: []
          }
        };
        
        // Early return with default structure if no crawl or analysis_results
        if (!crawl || !crawl.analysis_results) {
          console.log('No crawl data or analysis_results found');
          setResults(defaultKeywords);
          setIsLoading(false);
          return;
        }
        
        try {
          // Safe type casting - handle both string and object formats
          let analysisData: any;
          
          if (typeof crawl.analysis_results === 'string') {
            analysisData = JSON.parse(crawl.analysis_results);
            console.log('Successfully parsed string analysis_results');
          } else {
            analysisData = crawl.analysis_results;
            console.log('Using object analysis_results directly');
          }
          
          console.log('Analysis data keys:', Object.keys(analysisData));

          // Helper function to ensure we get a valid array
          const ensureArray = (possibleArray: any): string[] => {
            // Check if it's an array
            if (!Array.isArray(possibleArray)) return [];
            
            // Filter out empty strings, null, undefined, and convert non-strings to strings
            return possibleArray
              .filter(item => item !== null && item !== undefined && String(item).trim() !== '')
              .map(item => String(item).trim());
          };
          
          // Process keywords if they exist, otherwise use default empty arrays
          if (analysisData?.keywords) {
            console.log('Keywords data structure:', analysisData.keywords);
            
            // Handle both longTail and long_tail property names that might be in the data
            // Also ensure we have valid arrays and remove duplicates
            const keywords = {
              core: ensureArray(analysisData.keywords.core),
              competitive: ensureArray(analysisData.keywords.competitive),
              niche: ensureArray(analysisData.keywords.niche),
              longTail: ensureArray(
                analysisData.keywords.longTail || analysisData.keywords.long_tail
              )
            };
            
            // Remove potential duplicates across different categories
            // For example, a keyword might appear in both core and competitive sections
            const allKeywords = new Set<string>();
            const deduplicatedKeywords = {
              core: keywords.core.filter(kw => {
                if (allKeywords.has(kw)) return false;
                allKeywords.add(kw);
                return true;
              }),
              competitive: keywords.competitive.filter(kw => {
                if (allKeywords.has(kw)) return false;
                allKeywords.add(kw);
                return true;
              }),
              niche: keywords.niche.filter(kw => {
                if (allKeywords.has(kw)) return false;
                allKeywords.add(kw);
                return true;
              }),
              longTail: keywords.longTail.filter(kw => {
                if (allKeywords.has(kw)) return false;
                allKeywords.add(kw);
                return true;
              })
            };
            
            setResults({ keywords: deduplicatedKeywords });
          } else {
            console.log('No keywords found in analysis results');
            // Instead of setting to null, set to default structure with empty arrays
            setResults(defaultKeywords);
          }
        } catch (parseError) {
          console.error('Error parsing analysis results:', parseError);
          toast({
            title: "Data Processing Error",
            description: "There was an error processing the analysis data. Please try again or contact support.",
            variant: "destructive",
          });
          // Instead of setting to null, set to default structure with empty arrays
          setResults(defaultKeywords);
        }
      } catch (error) {
        console.error('Error fetching results:', error);
        toast({
          title: "Error",
          description: "Failed to fetch keywords analysis",
          variant: "destructive",
        });
        // Instead of setting to null, set to default structure with empty arrays
        setResults({
          keywords: {
            core: [],
            competitive: [],
            niche: [],
            longTail: []
          }
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchResults();
  }, [projectId, toast]);

  const copyToClipboard = (text: string, type: 'keyword' | 'group', id: string) => {
    if (!text) {
      toast({
        title: "Copy failed",
        description: "No content to copy",
        variant: "destructive",
      });
      return;
    }
    
    copyFormattedContent(text, {
      onSuccess: () => {
        setCopiedItem({ type, id });
        toast({
          title: "Copied to clipboard",
          description: `${type === 'keyword' ? 'Keyword' : 'Group'} has been copied to clipboard`,
        });
        setTimeout(() => setCopiedItem(null), 2000);
      },
      onError: (error) => {
        console.error("Copy failed:", error);
        toast({
          title: "Copy failed",
          description: "Failed to copy to clipboard",
          variant: "destructive",
        });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8 h-[calc(100vh-200px)]">
        <div className="animate-spin">
          <Loader className="h-8 w-8 text-blue-600 dark:text-blue-400" />
        </div>
      </div>
    );
  }

  // Change this to check if there are any keywords in the results
  const hasAnyData = results.keywords && (
    (results.keywords.core && results.keywords.core.length > 0) ||
    (results.keywords.competitive && results.keywords.competitive.length > 0) ||
    (results.keywords.niche && results.keywords.niche.length > 0) ||
    (results.keywords.longTail && results.keywords.longTail.length > 0)
  );

  // Show empty state only if we've confirmed there's no data
  if (!hasAnyData) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-950/20 dark:to-blue-950/20 rounded-xl p-8 shadow-md border border-blue-100 dark:border-blue-900/50">
          <div className="text-center py-8">
            <Hash className="h-14 w-14 text-blue-500 mx-auto mb-4 opacity-70" />
            <h2 className="text-2xl font-bold text-blue-700 mb-2">No Keywords Analysis Available</h2>
            <p className="text-zinc-600 dark:text-zinc-300 max-w-md mx-auto">
              Keywords analysis data is not available yet. Please analyze a website first to see keyword details.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Ensure we always have arrays even if they're undefined in the results
  const keywordSections = [
    { 
      title: "Core Keywords", 
      data: results.keywords?.core || [], 
      color: "teal", 
      description: [
        "Foundation terms (1-2 words) directly related to your main business offerings like 'SEO tools' or 'marketing software'",
        "These broad, competitive keywords establish topical authority and brand awareness",
        "Essential for capturing high-volume searches but require strong domain authority to rank",
        "Perfect for homepage optimization and brand building campaigns"
      ]
    },
    { 
      title: "Competitive Keywords", 
      data: results.keywords?.competitive || [], 
      color: "blue", 
      description: [
        "Keywords your competitors currently rank for, revealing market opportunities and content gaps",
        "Use these for competitor analysis, identifying trending topics in your industry, and capturing competitor traffic",
        "Valuable for PPC campaigns to intercept competitor searches and for content strategy to target underserved market segments"
      ]
    },
    { 
      title: "Niche & Feature-Based Keywords", 
      data: results.keywords?.niche || [], 
      color: "purple", 
      description: [
        "Specialized terms targeting unique features or specific market segments like 'AI-powered analytics dashboard' or 'GDPR-compliant email tools'",
        "Less competitive with higher relevance to specific user needs",
        "Ideal for targeting precise audiences, product pages, and establishing expertise in specialized areas",
        "Great for conversion-focused content"
      ]
    },
    { 
      title: "Long-Tail Keywords", 
      data: results.keywords?.longTail || [], 
      color: "emerald", 
      description: [
        "Longer, specific phrases (3-5+ words) like 'best SEO tools for small businesses' or 'how to improve website ranking 2024'",
        "Lower search volume but higher conversion rates and easier to rank for",
        "Perfect for voice search optimization, FAQ content, blog posts, and cost-effective PPC campaigns with better ROI"
      ]
    },
  ];

  // Calculate total keywords count
  const totalKeywords = keywordSections.reduce((total, section) => total + section.data.length, 0);
  
  // We don't need this check anymore as we handle it above
  // const hasAnyKeywords = totalKeywords > 0;

  return (
    <div className="flex flex-col min-h-screen bg-white dark:bg-zinc-900 animate-fadeIn">
      {/* Page Header */}
      <div className="border-b border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
        <div className="container mx-auto py-6 px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-zinc-900 dark:text-white">Keywords Analysis</h1>
              <p className="text-zinc-500 dark:text-zinc-400 mt-1">
                Discover core, competitive, niche, and long-tail keywords to improve your SEO and content strategy.
                {totalKeywords > 0 && <span className="ml-1">Found {totalKeywords} keywords across {keywordSections.filter(s => s.data.length > 0).length} categories.</span>}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 container mx-auto py-6 px-4 sm:px-6">
        {/* Keyword Sections */}
        {keywordSections.filter(section => section.data.length > 0).map((section, index) => (
          <div key={section.title} className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <h2 className={cn(
                  "text-xl font-semibold",
                  getColorClass(section.color, "text", "700"),
                  `dark:${getColorClass(section.color, "text", "300")}`
                )}>
                  {section.title}
                </h2>
                <ChevronRight className={cn(
                  "h-5 w-5 ml-2",
                  getColorClass(section.color, "text", "400"),
                  `dark:${getColorClass(section.color, "text", "300")}`
                )} />
              </div>
            </div>
            
            {/* Description Box */}
            <div className="mb-4 p-3 border border-zinc-200 dark:border-zinc-700 rounded-lg bg-zinc-50/50 dark:bg-zinc-800/30">
              <ul className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed space-y-1">
                {section.description.map((point, pointIndex) => (
                  <li key={pointIndex} className="flex items-start">
                    <span className="text-zinc-400 dark:text-zinc-500 mr-2">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {section.data.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {section.data.map((keyword: string, keywordIndex: number) => {
                  // Skip rendering if the keyword is not a valid string
                  if (typeof keyword !== 'string' || !keyword.trim()) {
                    return null;
                  }
                  
                  return (
                    <TooltipProvider key={keywordIndex}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div 
                            className={cn(
                              "px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 group relative transition-all shadow-sm",
                              section.color === "teal" ? "bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-900/40 dark:text-teal-200 dark:border-teal-800/50" : "",
                              section.color === "blue" ? "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800/50" : "",
                              section.color === "purple" ? "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800/50" : "",
                              section.color === "emerald" ? "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-800/50" : "",
                              "border",
                              "hover:shadow-md cursor-pointer transform hover:-translate-y-0.5 transition-all duration-150"
                            )}
                            onClick={() => copyToClipboard(keyword, 'keyword', keyword)}
                          >
                            <span>{keyword}</span>
                            <Copy className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Click to copy</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </div>
            ) : (
              <p className="text-zinc-500 dark:text-zinc-400 text-center py-4">No {section.title.toLowerCase()} available.</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default KeywordsPage;
